// ============================================
// ProgressBar — UKA-X01 D5
//
// Shows X/Y sections reviewed with percentage fill bar and
// collapsible category breakdown.
// No framer-motion. Pure CSS transitions.
// Min 16px font, WCAG AA contrast.
// Accent Rose #E11D48, BG #FAFAFA, Cards #FFFFFF, Text #18181B.
// ============================================

import React, { useState } from 'react';

export interface ProgressBarProps {
  total: number;
  reviewed: number;
  byCategory: Record<string, { total: number; reviewed: number }>;
}

export function ProgressBar({ total, reviewed, byCategory }: ProgressBarProps) {
  const [expanded, setExpanded] = useState(false);
  const pct = total > 0 ? Math.round((reviewed / total) * 100) : 0;

  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #E4E4E7',
        borderRadius: '4px',
        padding: '16px 20px',
        fontFamily: 'DM Sans, system-ui, sans-serif',
      }}
    >
      {/* Summary row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '10px',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        <span style={{ fontSize: '16px', fontWeight: 600, color: '#18181B' }}>
          {reviewed} / {total} sections reviewed
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '16px', fontWeight: 700, color: '#E11D48' }}>
            {pct}%
          </span>
          <button
            onClick={() => setExpanded((e) => !e)}
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse category breakdown' : 'Expand category breakdown'}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '13px',
              color: '#71717A',
              padding: '4px 0',
              fontFamily: 'DM Sans, system-ui, sans-serif',
              textDecoration: 'underline',
            }}
          >
            {expanded ? 'Hide ▲' : 'By category ▼'}
          </button>
        </div>
      </div>

      {/* Progress track */}
      <div
        role="progressbar"
        aria-valuenow={reviewed}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label={`${pct}% of sections reviewed`}
        style={{
          height: '8px',
          backgroundColor: '#E4E4E7',
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            backgroundColor: '#E11D48',
            borderRadius: '4px',
            transition: 'width 0.4s ease',
          }}
        />
      </div>

      {/* Category breakdown */}
      {expanded && (
        <div
          style={{
            marginTop: '16px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
            gap: '12px',
          }}
        >
          {Object.entries(byCategory).map(([label, { total: catTotal, reviewed: catReviewed }]) => {
            const catPct = catTotal > 0 ? Math.round((catReviewed / catTotal) * 100) : 0;
            return (
              <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '13px',
                    color: '#52525B',
                  }}
                >
                  <span>{label}</span>
                  <span style={{ fontWeight: 600, color: '#18181B' }}>
                    {catReviewed}/{catTotal}
                  </span>
                </div>
                <div
                  style={{
                    height: '4px',
                    backgroundColor: '#E4E4E7',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${catPct}%`,
                      backgroundColor: catPct === 100 ? '#16A34A' : '#E11D48',
                      borderRadius: '4px',
                      transition: 'width 0.4s ease',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
